import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { formatDateTime } from '../utils/dateFormat';

// Report catalog — keys match the backend /api/reports/:key endpoints.
const REPORTS = [
  { key: 'compliance', label: 'Check-in Compliance', columns: [
    ['device_id', 'Device'], ['patient_name', 'Patient'], ['slot_number', 'Slot'],
    ['completed', 'Completed'], ['missed', 'Missed'], ['medicine_taken', 'Meds'], ['compliance_rate', 'Compliance %'],
  ] },
  { key: 'alerts', label: 'Alerts', columns: [
    ['alert_type', 'Type'], ['severity', 'Severity'], ['total', 'Total'], ['resolved', 'Resolved'],
    ['pending', 'Pending'], ['avg_ack_minutes', 'Avg Ack (min)'], ['avg_resolve_minutes', 'Avg Resolve (min)'],
  ] },
  { key: 'ota', label: 'Firmware / OTA', columns: [
    ['version', 'Version'], ['board', 'Board'], ['status', 'Status'], ['count', 'Devices'],
  ] },
  { key: 'notifications', label: 'Notifications', columns: [
    ['channel', 'Channel'], ['status', 'Status'], ['count', 'Count'],
  ] },
  { key: 'device-health', label: 'Device Health', columns: [
    ['device_id', 'Device'], ['status', 'Status'], ['firmware_version', 'Firmware'], ['board_model', 'Board'],
    ['last_seen_at', 'Last Seen'], ['avg_signal', 'Avg Signal'], ['avg_uptime_hours', 'Avg Uptime (h)'], ['samples', 'Samples'],
  ] },
  { key: 'subscriptions', label: 'Subscriptions', columns: [
    ['plan', 'Plan'], ['status', 'Status'], ['count', 'Devices'], ['total_billing', 'Total Billing'],
  ] },
];

const isoDate = (d) => d.toISOString().slice(0, 10);

export default function Reports() {
  const [active, setActive] = useState('compliance');
  const [from, setFrom] = useState(() => isoDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)));
  const [to, setTo] = useState(() => isoDate(new Date()));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const report = REPORTS.find((r) => r.key === active);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/reports/${active}`, { params: { from, to } });
      setRows(data.rows || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load report');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [active, from, to]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/reports/${active}`, {
        params: { from, to, format: 'csv' },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${active}-report-${from}_to_${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      window.alert('Failed to export CSV');
    }
  };

  const renderCell = (key, value) => {
    if (value === null || value === undefined || value === '') return '--';
    if (key === 'last_seen_at') return formatDateTime(value);
    if (key === 'compliance_rate') return `${value}%`;
    if (key === 'total_billing') return `$${value}`;
    return String(value);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Reports</h1>
          <p className="text-secondary-500 text-sm mt-1">Operational reporting across the fleet</p>
        </div>
        <button
          onClick={exportCsv}
          disabled={loading || rows.length === 0}
          className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition disabled:opacity-50"
        >
          Export CSV
        </button>
      </div>

      {/* Report selector */}
      <div className="flex flex-wrap gap-2">
        {REPORTS.map((r) => (
          <button
            key={r.key}
            onClick={() => setActive(r.key)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition ${
              active === r.key
                ? 'bg-primary-600 text-white'
                : 'bg-white text-secondary-600 border border-secondary-200 hover:bg-secondary-50'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Date range */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-secondary-500 font-medium">From</label>
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)}
            className="px-3 py-1.5 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-secondary-500 font-medium">To</label>
          <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)}
            className="px-3 py-1.5 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none" />
        </div>
        <p className="text-xs text-secondary-400 pb-2">
          {report?.key === 'subscriptions' ? 'Subscriptions report is point-in-time (date range ignored).' : ''}
        </p>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary-50 text-secondary-600">
              <tr>
                {report.columns.map(([key, label]) => (
                  <th key={key} className="px-4 py-2.5 text-left font-medium whitespace-nowrap">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {loading && (
                <tr><td colSpan={report.columns.length} className="px-4 py-8 text-center text-secondary-400">Loading...</td></tr>
              )}
              {!loading && error && (
                <tr><td colSpan={report.columns.length} className="px-4 py-8 text-center text-red-500">{error}</td></tr>
              )}
              {!loading && !error && rows.length === 0 && (
                <tr><td colSpan={report.columns.length} className="px-4 py-8 text-center text-secondary-400">No data for this range</td></tr>
              )}
              {!loading && !error && rows.map((row, i) => (
                <tr key={i} className={i % 2 ? 'bg-secondary-50/40' : ''}>
                  {report.columns.map(([key]) => (
                    <td key={key} className="px-4 py-2 text-secondary-700 whitespace-nowrap">{renderCell(key, row[key])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
